import { Indexed, indexedObjectFromArray } from "../../utils/asyncTools"
import { buyOrSell } from "../../utils/company"
import axios from "axios"
import { Dispatch } from "../store.config"
import { selectNewAccountAction } from "./inputMask.ducks"
import { AnyAction } from "redux"

export interface Account {
  auxiliary_details: string | null
  auxiliary_number: string | null
  company_id: number
  construction_reverse_vat: boolean
  construction_vat: boolean
  created_at: string
  details: string
  deactivated_at: string
  eu_reverse_vat: boolean
  eu_vat: boolean
  id: number
  number: string
  updated_at: string
  deactivated_admin_user_id: string
}

const enum AccountActionsEnum {
  LOAD_PNL_ACCOUNTS_ATTEMPT = "ACCOUNTS/LoadPNLAccountsAttempt",
  LOAD_PNL_ACCOUNTS_SUCCESS = "ACCOUNTS/LoadPNLAccountsSuccess",
  LOAD_PNL_ACCOUNTS_FAILURE = "ACCOUNTS/LoadPNLAccountsFailure",

  LOAD_ACCOUNTS_ATTEMPT = "ACCOUNTS/LoadAccountsAttempt",
  LOAD_ACCOUNTS_SUCCESS = "ACCOUNTS/LoadAccountsSuccess",
  LOAD_ACCOUNTS_FAILURE = "ACCOUNTS/LoadAccountsFailure",

  CREATE_OR_UPDATE_ACCOUNT_RESET_ACTION = "ACCOUNTS/CreateOrUpdateResetAction",
  CREATE_ACCOUNT_ATTEMPT_ACTION = "ACCOUNTS/CreateAccountAttemptAction",
  CREATE_ACCOUNT_SUCCESS_ACTION = "ACCOUNTS/CreateAccountSuccessAction",
  CREATE_ACCOUNT_FAILURE_ACTION = "ACCOUNTS/CreateAccountFailureAction",

  UPDATE_ACCOUNT_ATTEMPT_ACTION = "ACCOUNTS/UpdateAccountAttemptAction",
  UPDATE_ACCOUNT_SUCCESS_ACTION = "ACCOUNTS/UpdateAccountSuccessAction",
  UPDATE_ACCOUNT_FAILURE_ACTION = "ACCOUNTS/UpdateAccountFailureAction",

  GET_ACCOUNT_INSTRUCTIONS_HISTORY_ATTEMPT = "ACCOUNTS/getAccountInstructionsHistoryAttempt",
  GET_ACCOUNT_INSTRUCTIONS_HISTORY_SUCCESS = "ACCOUNTS/getAccountInstructionsHistorySuccess",
  GET_ACCOUNT_INSTRUCTIONS_HISTORY_FAILURE = "ACCOUNTS/getAccountInstructionsHistoryFailure",

  REACTIVATE_ACCOUNT_ATTEMPT = "ACCOUNTS/reactivateAccountAttempt",
  REACTIVATE_ACCOUNT_SUCCESS = "ACCOUNTS/reactivateAccountSuccess",
  REACTIVATE_ACCOUNT_FAILURE = "ACCOUNTS/reactivateAccountFailure",

  DEACTIVATE_ACCOUNT_ATTEMPT = "ACCOUNTS/deactivateAccountAttempt",
  DEACTIVATE_ACCOUNT_SUCCESS = "ACCOUNTS/deactivateAccountSuccess",
  DEACTIVATE_ACCOUNT_FAILURE = "ACCOUNTS/deactivateAccountFailure",
  DEACTIVATE_ACCOUNT_MERCHANT_CODE_DISABLED = "ACCOUNTS/deactivateAccountMerchantCodeDisabled",

  DEACTIVATE_OR_REACTIVATE_ACCOUNT_RESET = "ACCOUNTS/deactivateOrReactivateAccountReset",
}

export const loadPNLAccountsAttemptAction = () =>
  ({ type: AccountActionsEnum.LOAD_PNL_ACCOUNTS_ATTEMPT } as const)
export const loadPNLAccountsSuccessAction = (payload: {
  buy: Indexed<Account>
  sell: Indexed<Account>
}) => ({ type: AccountActionsEnum.LOAD_PNL_ACCOUNTS_SUCCESS, payload } as const)
export const loadPNLAccountsFailureAction = (error: Error) =>
  ({
    type: AccountActionsEnum.LOAD_PNL_ACCOUNTS_FAILURE,
    error: error,
  } as const)

export const loadAccountsAttemptAction = () =>
  ({ type: AccountActionsEnum.LOAD_ACCOUNTS_ATTEMPT } as const)
export const loadAccountsSuccessAction = (payload: Account[]) =>
  ({ type: AccountActionsEnum.LOAD_ACCOUNTS_SUCCESS, payload } as const)
export const loadAccountsFailureAction = (error: Error) =>
  ({ type: AccountActionsEnum.LOAD_ACCOUNTS_FAILURE, error: error } as const)

export const createAccountAttemptAction = () =>
  ({
    type: AccountActionsEnum.CREATE_ACCOUNT_ATTEMPT_ACTION,
  } as const)
export const createOrUpdateAccountResetAction = () =>
  ({
    type: AccountActionsEnum.CREATE_OR_UPDATE_ACCOUNT_RESET_ACTION,
  } as const)
export const createAccountSuccessAction = ({
  newAccount,
}: {
  newAccount: Account
}) =>
  ({
    type: AccountActionsEnum.CREATE_ACCOUNT_SUCCESS_ACTION,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey: "accounting-plan.accounts.modal.create.success.title",
      bodyKey: "accounting-plan.accounts.modal.create.success.body",
    },
    payload: newAccount,
  } as const)
export const createAccountFailureAction = (error: string) =>
  ({
    type: AccountActionsEnum.CREATE_ACCOUNT_FAILURE_ACTION,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: "accounting-plan.accounts.modal.create.error.title",
      bodyKey: getAccountErrorId(error),
    },
  } as const)

export const updateAccountAttemptAction = () =>
  ({
    type: AccountActionsEnum.UPDATE_ACCOUNT_ATTEMPT_ACTION,
  } as const)
export const updateAccountSuccessAction = () =>
  ({
    type: AccountActionsEnum.UPDATE_ACCOUNT_SUCCESS_ACTION,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey: "accounting-plan.accounts.modal.modify.success.title",
      bodyKey: "accounting-plan.accounts.modal.modify.success.body",
    },
  } as const)
export const updateAccountFailureAction = (error: string) =>
  ({
    type: AccountActionsEnum.UPDATE_ACCOUNT_FAILURE_ACTION,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: "accounting-plan.accounts.modal.modify.error.title",
      bodyKey: getAccountErrorId(error),
    },
  } as const)

export const getAccountInstructionsHistoryAttempt = () =>
  ({
    type: AccountActionsEnum.GET_ACCOUNT_INSTRUCTIONS_HISTORY_ATTEMPT,
  } as const)
export const getAccountInstructionsHistorySuccess = (
  payload: AccountInstruction[]
) =>
  ({
    type: AccountActionsEnum.GET_ACCOUNT_INSTRUCTIONS_HISTORY_SUCCESS,
    payload,
  } as const)
export const getAccountInstructionsHistoryFailure = (error: Error) =>
  ({
    type: AccountActionsEnum.GET_ACCOUNT_INSTRUCTIONS_HISTORY_FAILURE,
    error: error,
  } as const)

export const reactivateAccountAttempt = () =>
  ({ type: AccountActionsEnum.REACTIVATE_ACCOUNT_ATTEMPT } as const)
export const reactivateAccountSuccess = () =>
  ({
    type: AccountActionsEnum.REACTIVATE_ACCOUNT_SUCCESS,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey: "accounting-plan.accounts.reactivate.success.title",
      bodyKey: "accounting-plan.accounts.reactivate.success.body",
    },
  } as const)
export const reactivateAccountFailure = (error: string) =>
  ({
    type: AccountActionsEnum.REACTIVATE_ACCOUNT_FAILURE,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: "accounting-plan.accounts.reactivate.error.title",
      bodyKey: getAccountErrorId(error),
    },
  } as const)

export const deactivateAccountAttempt = () =>
  ({ type: AccountActionsEnum.DEACTIVATE_ACCOUNT_ATTEMPT } as const)
export const deactivateAccountSuccess = () =>
  ({
    type: AccountActionsEnum.DEACTIVATE_ACCOUNT_SUCCESS,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey: "accounting-plan.accounts.deactivate.success.title",
      bodyKey: "accounting-plan.accounts.deactivate.success.body",
    },
  } as const)
export const deactivateAccountFailure = (
  error: string,
  number: string,
  merchantName: string
) =>
  ({
    type: AccountActionsEnum.DEACTIVATE_ACCOUNT_FAILURE,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: "accounting-plan.accounts.deactivate.error.title",
      bodyKey: getAccountErrorId(error),
      bodyValues: {
        accountNumber: number,
        merchantName: merchantName,
      },
    },
  } as const)

export const deactivateOrReactivateAccountReset = () =>
  ({ type: AccountActionsEnum.DEACTIVATE_OR_REACTIVATE_ACCOUNT_RESET } as const)

const getAccountErrorId = (errorMessage: string) => {
  let id = "error"

  if (errorMessage.includes("comme compte de Tiers sur votre dossier")) {
    id = "accounting-plan.accounts.deactivate.error.third-account.company"
  } else if (errorMessage.includes("il reste un PnlAccount")) {
    id = "accounting-plan.accounts.deactivate.error.pnl-account"
  } else if (errorMessage.includes("utilisé pour la TVA de votre dossier")) {
    id = "accounting-plan.accounts.deactivate.error.vat-account"
  } else if (
    errorMessage.includes("comme compte par défaut sur le fournisseur")
  ) {
    id = "accounting-plan.accounts.deactivate.error.merchant.default.buy"
  } else if (errorMessage.includes("comme compte par défaut sur le client")) {
    id = "accounting-plan.accounts.deactivate.error.merchant.default.sell"
  } else if (errorMessage.includes("comme compte de TVA sur le fournisseur")) {
    id = "accounting-plan.accounts.deactivate.error.merchant.vat.buy"
  } else if (errorMessage.includes("comme compte de TVA sur le client")) {
    id = "accounting-plan.accounts.deactivate.error.merchant.vat.sell"
  } else if (errorMessage.includes("autoliquidée déductible intraco")) {
    id = "accounting-plan.accounts.deactivate.error.company.eu-vat"
  } else if (errorMessage.includes("autoliquidée collectée intraco")) {
    id = "accounting-plan.accounts.deactivate.error.company.eu-vat-reverse"
  } else if (errorMessage.includes("autoliquidée déductible BTP")) {
    id = "accounting-plan.accounts.deactivate.error.company.construction"
  } else if (errorMessage.includes("autoliquidée collectée BTP")) {
    id =
      "accounting-plan.accounts.deactivate.error.company.construction-reverse"
  } else if (
    errorMessage.includes(
      "Deactivator_user_admin and deactivator_user cant be not nil"
    )
  ) {
    id = "accounting-plan.accounts.deactivate.error.one-deactivator"
  } else if (errorMessage.includes("be unique per company")) {
    id = "accounting-plan.accounts.modal.create.error.body.unique-number"
  } else if (errorMessage.includes("Couldn't find Account with")) {
    id = "accounting-plan.accounts.error.no-account-found"
  } else if (
    errorMessage.includes("Vous devez renseigner les détails pour ce compte")
  ) {
    id = "accounting-plan.accounts.modal.create.error.body.details.empty"
  } else if (
    errorMessage.includes(
      "Vous devez renseigner un numéro d'identification pour ce compte"
    )
  ) {
    id = "accounting-plan.accounts.modal.create.error.body.number.empty"
  }

  return id
}

type AccountsActionsType = ReturnType<
  | typeof loadPNLAccountsSuccessAction
  | typeof loadPNLAccountsFailureAction
  | typeof loadPNLAccountsAttemptAction
  | typeof loadAccountsSuccessAction
  | typeof loadAccountsFailureAction
  | typeof loadAccountsAttemptAction
  | typeof createAccountAttemptAction
  | typeof createAccountSuccessAction
  | typeof createAccountFailureAction
  | typeof createOrUpdateAccountResetAction
  | typeof updateAccountAttemptAction
  | typeof updateAccountSuccessAction
  | typeof updateAccountFailureAction
  | typeof getAccountInstructionsHistoryAttempt
  | typeof getAccountInstructionsHistorySuccess
  | typeof getAccountInstructionsHistoryFailure
  | typeof reactivateAccountAttempt
  | typeof reactivateAccountSuccess
  | typeof reactivateAccountFailure
  | typeof deactivateAccountAttempt
  | typeof deactivateAccountSuccess
  | typeof deactivateAccountFailure
  | typeof deactivateOrReactivateAccountReset
>

export interface AccountsState {
  PNL: {
    [BOS in buyOrSell]: {
      [index: number]: Account
    }
  }
  PNLAccountsStatus: "UNSET" | "LOADING" | "SUCCESS" | "FAILURE"
  all_accounts: Account[]
  getAllAccountsStatus: "UNSET" | "LOADING" | "SUCCESS" | "FAILURE"
  accountCreationOrUpdateStatus: "UNSET" | "LOADING" | "SUCCESS" | "FAILURE"
  accountInstructionsHistory: AccountInstruction[]
  reactivateOrDeactivateAccountStatus:
    | "UNSET"
    | "SUCCESS"
    | "LOADING"
    | "FAILURE"
}
export const accountsInitialState: AccountsState = {
  PNL: {
    buy: {},
    sell: {},
  },
  PNLAccountsStatus: "UNSET",
  all_accounts: [],
  getAllAccountsStatus: "UNSET",
  accountCreationOrUpdateStatus: "UNSET",
  accountInstructionsHistory: [],
  reactivateOrDeactivateAccountStatus: "UNSET",
}

export function accountsReducer(
  state = accountsInitialState,
  action: AccountsActionsType
): AccountsState {
  switch (action.type) {
    case AccountActionsEnum.LOAD_PNL_ACCOUNTS_SUCCESS:
      return { ...state, PNL: action.payload, PNLAccountsStatus: "SUCCESS" }
    case AccountActionsEnum.LOAD_PNL_ACCOUNTS_ATTEMPT:
      return { ...state, PNLAccountsStatus: "LOADING" }
    case AccountActionsEnum.LOAD_PNL_ACCOUNTS_FAILURE:
      return { ...state, PNLAccountsStatus: "FAILURE" }
    case AccountActionsEnum.LOAD_ACCOUNTS_SUCCESS:
      return {
        ...state,
        all_accounts: action.payload,
        getAllAccountsStatus: "SUCCESS",
      }
    case AccountActionsEnum.LOAD_ACCOUNTS_ATTEMPT:
      return { ...state, getAllAccountsStatus: "LOADING" }
    case AccountActionsEnum.LOAD_ACCOUNTS_FAILURE:
      return { ...state, getAllAccountsStatus: "FAILURE" }

    case AccountActionsEnum.CREATE_OR_UPDATE_ACCOUNT_RESET_ACTION:
      return { ...state, accountCreationOrUpdateStatus: "UNSET" }
    case AccountActionsEnum.CREATE_ACCOUNT_ATTEMPT_ACTION:
      return { ...state, accountCreationOrUpdateStatus: "LOADING" }
    case AccountActionsEnum.CREATE_ACCOUNT_FAILURE_ACTION:
      return { ...state, accountCreationOrUpdateStatus: "FAILURE" }
    case AccountActionsEnum.CREATE_ACCOUNT_SUCCESS_ACTION:
      return {
        ...state,
        accountCreationOrUpdateStatus: "SUCCESS",
        all_accounts: [...state.all_accounts, action.payload],
      }
    case AccountActionsEnum.UPDATE_ACCOUNT_ATTEMPT_ACTION:
      return { ...state, accountCreationOrUpdateStatus: "LOADING" }
    case AccountActionsEnum.UPDATE_ACCOUNT_FAILURE_ACTION:
      return { ...state, accountCreationOrUpdateStatus: "FAILURE" }
    case AccountActionsEnum.UPDATE_ACCOUNT_SUCCESS_ACTION:
      return {
        ...state,
        accountCreationOrUpdateStatus: "SUCCESS",
      }
    case AccountActionsEnum.REACTIVATE_ACCOUNT_ATTEMPT:
      return { ...state, reactivateOrDeactivateAccountStatus: "LOADING" }
    case AccountActionsEnum.REACTIVATE_ACCOUNT_FAILURE:
      return { ...state, reactivateOrDeactivateAccountStatus: "FAILURE" }
    case AccountActionsEnum.REACTIVATE_ACCOUNT_SUCCESS:
      return {
        ...state,
        reactivateOrDeactivateAccountStatus: "SUCCESS",
      }
    case AccountActionsEnum.DEACTIVATE_ACCOUNT_ATTEMPT:
      return { ...state, reactivateOrDeactivateAccountStatus: "LOADING" }
    case AccountActionsEnum.DEACTIVATE_ACCOUNT_FAILURE:
      return { ...state, reactivateOrDeactivateAccountStatus: "FAILURE" }
    case AccountActionsEnum.DEACTIVATE_ACCOUNT_SUCCESS:
      return {
        ...state,
        reactivateOrDeactivateAccountStatus: "SUCCESS",
      }
    case AccountActionsEnum.DEACTIVATE_OR_REACTIVATE_ACCOUNT_RESET:
      return { ...state, reactivateOrDeactivateAccountStatus: "UNSET" }
    case AccountActionsEnum.GET_ACCOUNT_INSTRUCTIONS_HISTORY_SUCCESS:
      return { ...state, accountInstructionsHistory: action.payload }
    default:
      return { ...state }
  }
}

export const loadPNLAccountsThunk =
  (companyId: number) => (dispatch: Dispatch<AccountsActionsType>) => {
    dispatch(loadPNLAccountsAttemptAction())
    return axios
      .get<{ buy_accounts: Array<Account>; sell_accounts: Array<Account> }>(
        `/companies/accounting_data_of_company`,
        // all accounts are always present regardless of buy/sell
        // buy/sell is only useful when loading merchants
        { params: { buy_or_sell: "buy", id: companyId } }
      )
      .then((response) => {
        return {
          buy: indexedObjectFromArray(response.data.buy_accounts),
          sell: indexedObjectFromArray(response.data.sell_accounts),
        }
      })
      .then((data) => dispatch(loadPNLAccountsSuccessAction(data)))
      .catch((error) => dispatch(loadPNLAccountsFailureAction(error)))
  }

export const getAllAccountsThunk =
  (companyId: number) => (dispatch: Dispatch<AccountsActionsType>) => {
    return axios
      .get<{ accounts: Account[] }>(`/accounts/get_all_accounts`, {
        params: { company_id: companyId },
      })
      .then((response) =>
        dispatch(loadAccountsSuccessAction(response.data.accounts))
      )
      .catch((error) => dispatch(loadAccountsFailureAction(error)))
  }

interface CreateAccountResponse {
  auto_select_buy: boolean
  auto_select_sell: boolean
  new_account: Account
}
export const createAccountThunk =
  (
    companyId: number,
    number: string,
    details: string,
    writingLineUuid?: string
  ) =>
  (dispatch: Dispatch<AnyAction>) => {
    dispatch(createAccountAttemptAction())
    return axios
      .post<CreateAccountResponse>(`/accounts`, {
        company_id: companyId,
        account_number: number,
        account_details: details,
      })
      .then(({ data: { new_account, auto_select_buy, auto_select_sell } }) => {
        dispatch(
          createAccountSuccessAction({
            newAccount: new_account,
          })
        )
        if (auto_select_buy || auto_select_sell) {
          dispatch(
            selectNewAccountAction({
              buyOrsell: auto_select_buy ? "buy" : "sell",
              newAccount: new_account,
              writingLineUuid,
            })
          )
        }
      })
      .catch((error) =>
        dispatch(createAccountFailureAction(error.response.data.error))
      )
  }

export const updateAccountThunk =
  (
    companyId: number,
    accountId: number,
    newNumber: string,
    oldNumber: string,
    newDetails: string,
    oldDetails: string
  ) =>
  (dispatch: Dispatch<AccountsActionsType>) => {
    dispatch(updateAccountAttemptAction())
    return axios
      .put<{ accounts: Account[] }>(`/accounts/${accountId}`, {
        company_id: companyId,
        new_account_number: newNumber,
        old_account_number: oldNumber,
        new_account_details: newDetails,
        old_account_details: oldDetails,
      })
      .then(() => dispatch(updateAccountSuccessAction()))
      .catch((error) => {
        dispatch(updateAccountFailureAction(error.response.data.error))
      })
  }

export interface AccountInstruction {
  created_at: string
  instruction_type: string
  email: string
  metadata: { old_account_number: string; old_account_details: string }
}

export const getAccountInstructionsHistory =
  (companyId: number, accountId: number) =>
  (dispatch: Dispatch<AccountsActionsType>) => {
    dispatch(getAccountInstructionsHistoryAttempt())
    return axios
      .get<AccountInstruction[]>(`/accounts/get_account_instructions_history`, {
        params: { company_id: companyId, account_id: accountId },
      })
      .then((response) =>
        dispatch(getAccountInstructionsHistorySuccess(response.data))
      )
      .catch((error) => dispatch(getAccountInstructionsHistoryFailure(error)))
  }

export const reactivateAccountThunk =
  (companyId: number, currentAccount: Account) =>
  (dispatch: Dispatch<AccountsActionsType>) => {
    dispatch(reactivateAccountAttempt())
    return axios
      .put(`/accounts/reactivate_account`, {
        company_id: companyId,
        account_id: currentAccount.id,
        old_account_number: currentAccount.number,
      })
      .then(() => dispatch(reactivateAccountSuccess()))
      .catch((error) =>
        dispatch(reactivateAccountFailure(error.response.data.error))
      )
  }

export const deactivateAccountThunk =
  (companyId: number, currentAccount: Account) =>
  (dispatch: Dispatch<AccountsActionsType>) => {
    dispatch(deactivateAccountAttempt())
    return axios
      .put<{ merchantCode: string }>(`/accounts/deactivate_account`, {
        company_id: companyId,
        account_id: currentAccount.id,
        old_account_number: currentAccount.number,
      })
      .then((response) => {
        dispatch(deactivateAccountSuccess())
      })
      .catch((error) => {
        let merchantName = ""
        if (
          error.response.data.error.includes("compte de TVA") ||
          error.response.data.error.includes("compte par défaut")
        ) {
          merchantName = error.response.data.merchantName
        }
        dispatch(
          deactivateAccountFailure(
            error.response.data.error,
            currentAccount.number,
            merchantName
          )
        )
      })
  }
